import React, { useState } from "react";
import { Mail, MessageCircle, Globe, Send, Star, Clock } from "lucide-react";
import { useCurrency } from "../contexts/CurrencyContext";

const SERVICES = (price) => [
  { name: "5-Minute Free Chat (New User)", duration: "5 min", price: "Free" },
  { name: "Detailed Reading", duration: "15 min", price: "₹251" },
  { name: "AI Chat Plan", duration: "24 hr · 4 questions", price: price("ai_chat") },
  { name: "15-Minute Live Consultation", duration: "15 min", price: price("consultation") },
  { name: "Compatibility (Match) Report", duration: "Instant PDF", price: price("match") },
  { name: "1-to-1 Live Video Session", duration: "1 hr", price: "₹5,001" },
  { name: "Monthly Subscription", duration: "Up to 10 hr", price: "Contact us" },
];

export default function ContactPage() {
  const { price } = useCurrency();
  const services = SERVICES(price);
  const [sent, setSent] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", message: "" });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Open default mail client with prefilled message
    const subject = encodeURIComponent(`AstroFate enquiry from ${form.name}`);
    const body = encodeURIComponent(`Name: ${form.name}\nEmail: ${form.email}\n\n${form.message}`);
    window.location.href = `mailto:info@theastrofate.com?subject=${subject}&body=${body}`;
    setSent(true);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#120021", paddingTop: "80px" }}>
      <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0 }}>
        <div style={{ position: "absolute", width: "500px", height: "500px", background: "radial-gradient(circle, rgba(91,30,140,0.18), transparent)", top: "10%", right: "5%", borderRadius: "50%" }} />
      </div>

      <div style={{ maxWidth: "960px", margin: "0 auto", padding: "0 16px 60px", position: "relative", zIndex: 1 }}>
        <div className="animate-fadeInUp" style={{ textAlign: "center", marginBottom: "40px" }}>
          <h1 className="font-cinzel gradient-text-gold" style={{ fontSize: "clamp(1.8rem, 3vw, 2.6rem)", marginBottom: "10px" }}>Contact Us</h1>
          <p style={{ color: "rgba(255,255,255,0.55)", fontSize: "0.95rem" }}>
            Reach out anytime — we answer most queries within 24 hours.
          </p>
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "20px", marginBottom: "40px" }} className="contact-grid">
          {/* Contact card */}
          <div className="glass-card animate-fadeInUp delay-100" style={{ padding: "28px" }}>
            <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.2rem", marginBottom: "20px" }}>Get in Touch</h2>
            <div style={{ display: "flex", flexDirection: "column", gap: "18px" }}>
              <a href="mailto:info@theastrofate.com" data-testid="contact-email" style={{ display: "flex", alignItems: "center", gap: "12px", color: "rgba(255,255,255,0.85)", textDecoration: "none" }}>
                <span style={{ background: "rgba(255,209,102,0.1)", padding: "10px", borderRadius: "10px", display: "flex" }}>
                  <Mail size={18} style={{ color: "#FFD166" }} />
                </span>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>Email</div>
                  <div style={{ fontSize: "0.9rem" }}>info@theastrofate.com</div>
                </div>
              </a>

              <a href="https://www.theastrofate.com" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: "12px", color: "rgba(255,255,255,0.85)", textDecoration: "none" }}>
                <span style={{ background: "rgba(157,78,221,0.1)", padding: "10px", borderRadius: "10px", display: "flex" }}>
                  <Globe size={18} style={{ color: "#c084fc" }} />
                </span>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>Website</div>
                  <div style={{ fontSize: "0.9rem" }}>theastrofate.com</div>
                </div>
              </a>

              <a data-testid="contact-whatsapp" href="https://wa.me/918145822791" target="_blank" rel="noreferrer" style={{ display: "flex", alignItems: "center", gap: "12px", color: "rgba(255,255,255,0.85)", textDecoration: "none" }}>
                <span style={{ background: "rgba(34,197,94,0.1)", padding: "10px", borderRadius: "10px", display: "flex" }}>
                  <MessageCircle size={18} style={{ color: "#86efac" }} />
                </span>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>WhatsApp</div>
                  <div style={{ fontSize: "0.9rem" }}>+91 81458 22791</div>
                </div>
              </a>

              <div style={{ display: "flex", alignItems: "center", gap: "12px", color: "rgba(255,255,255,0.85)" }}>
                <span style={{ background: "rgba(255,209,102,0.1)", padding: "10px", borderRadius: "10px", display: "flex" }}>
                  <Clock size={18} style={{ color: "#FFD166" }} />
                </span>
                <div>
                  <div style={{ color: "rgba(255,255,255,0.4)", fontSize: "0.72rem" }}>Response Hours (IST)</div>
                  <div style={{ fontSize: "0.9rem" }}>Mon – Sat, 9:00 AM – 9:00 PM</div>
                </div>
              </div>
            </div>
          </div>

          {/* Quick form */}
          <div className="glass-card animate-fadeInUp delay-200" style={{ padding: "28px" }}>
            <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.2rem", marginBottom: "20px" }}>Send a Message</h2>
            {sent ? (
              <div style={{ textAlign: "center", padding: "20px 0" }}>
                <Star size={32} style={{ color: "#86efac", margin: "0 auto 12px", display: "block" }} fill="#86efac" />
                <p style={{ color: "rgba(255,255,255,0.8)", fontSize: "0.92rem" }}>Your mail client has been opened.</p>
                <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.82rem", marginTop: "6px" }}>Hit send there and we'll reply within 24 hours.</p>
                <button onClick={() => setSent(false)} className="btn-ghost" style={{ marginTop: "20px", padding: "8px 16px", fontSize: "0.85rem" }}>Send another</button>
              </div>
            ) : (
              <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                <input data-testid="contact-name-input" type="text" placeholder="Your name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} required className="cosmic-input" />
                <input data-testid="contact-email-input" type="email" placeholder="Your email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} required className="cosmic-input" />
                <textarea data-testid="contact-message-input" placeholder="How can we help?" rows={4} value={form.message} onChange={e => setForm({...form, message: e.target.value})} required className="cosmic-input" style={{ resize: "vertical" }} />
                <button data-testid="contact-submit-btn" type="submit" className="btn-gold" style={{ padding: "10px 18px", display: "inline-flex", alignItems: "center", justifyContent: "center", gap: "8px" }}>
                  <Send size={14} /> Send Message
                </button>
              </form>
            )}
          </div>
        </div>

        {/* Services */}
        <div className="glass-card animate-fadeInUp delay-300" style={{ padding: "28px" }}>
          <h2 className="font-cinzel" style={{ color: "#FFD166", fontSize: "1.2rem", marginBottom: "20px" }}>Services & Pricing</h2>
          <p style={{ color: "rgba(255,255,255,0.5)", fontSize: "0.85rem", marginBottom: "20px" }}>All prices in Indian Rupees (INR). Goods and Services Tax (GST), where applicable, will be added at checkout.</p>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(220px, 1fr))", gap: "14px" }}>
            {services.map((s, i) => (
              <div key={i} data-testid={`service-card-${i}`} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: "12px", padding: "16px" }}>
                <div style={{ color: "rgba(255,255,255,0.9)", fontSize: "0.92rem", fontWeight: "600", marginBottom: "6px" }}>{s.name}</div>
                <div style={{ color: "rgba(255,255,255,0.45)", fontSize: "0.75rem", marginBottom: "10px" }}>{s.duration}</div>
                <div style={{ color: "#FFD166", fontSize: "1.05rem", fontWeight: "700" }}>{s.price}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 720px) {
          .contact-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}
